import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { shouldShowLegacyReportSettings } from "@utils/FeatureFlags/selfServeAccess";
import { scrollToElementById, useNavigateById } from "@utils/utils";
import clsx from "clsx";
import { useFlags } from "launchdarkly-react-client-sdk";
import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { Dialog, Paragraph, SelectItem } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportTypeChoices,
  ApiReportTemplateExportFrameworkChoices,
  ExportTypeEnum,
  LearnerFileType,
} from "@api/graphql/types-and-hooks";
import { OffscreenChartCaptureRunner } from "@components/ChartCapture/OffscreenChartCaptureRunner";
import { isFieldContentEmpty } from "@components/FieldComponent/utils";
import { LayoutAside, LayoutBody, LayoutMain } from "@components/Layout";
import { PreThinkingScreen } from "@components/QaReview/components/PreThinkingScreen";
import { ReviewBar } from "@components/QaReview/components/ReviewBar";
import { ReviewColumn } from "@components/QaReview/components/ReviewColumn";
import { BlankState, SkeletonState } from "@components/QaReview/components/ReviewStates";
import { SuggestionDisclaimer } from "@components/QaReview/components/SuggestionDisclaimer";
import { useFirstTimeDisclaimer } from "@components/QaReview/hooks/useFirstTimeDisclaimer";
import { useIsQaScannerEnabled } from "@components/QaReview/hooks/useIsQaScannerEnabled";
import { useIsReviewPanelVisible } from "@components/QaReview/hooks/useIsReviewPanelVisible";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import { useReviewGate } from "@components/QaReview/hooks/useReviewGate";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { ReportCompletionDialog } from "@components/ReportCompletionAndExport/ReportCompletionDialog";
import { EXPORT_OPTIONS, ReportExportDialog } from "@components/ReportCompletionAndExport/ReportExportDialog";
import { ReportSection } from "@components/ReportField/ReportSection";
import { ReportSettings } from "@components/ReportSettings/ReportSettings";
import { notifyWarn } from "@components/notifications/notifications";
import { useReassessmentChartBackfill } from "@hooks/useReassessmentChartBackfill";
import {
  computeTotalDaysToComplete,
  countInsuranceFlagsRemaining,
  useReportAnalytics,
} from "@hooks/useReportAnalytics";
import { useReportExport } from "@hooks/useReportExport";
import { useScrollSync } from "@hooks/useScrollSync";
import { useSignatureHandler } from "@hooks/useSignatureHandler";
import { useTemplateExport } from "@hooks/useTemplateExport";
import { AssessmentReportSearch } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/AssessmentReportSearch";
import { AssessmentReportsCollapsible } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/AssessmentReportsCollapsible";
import { AssessmentSlideOver } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/AssessmentSlideOver";
import { PreviousAssessmentReportCollapsible } from "@pages/ClinicalReportDetails/Components/AssessmentFiles/PreviousAssessmentReportCollapsible";
import { ClinicalReportFooterButtonRow } from "@pages/ClinicalReportDetails/Components/ClinicalReportFooterButtonRow";
import { StickyNavCollapsible } from "@pages/ClinicalReportDetails/Components/StickyNavCollapsible";
import { FocusedFieldManagerProvider } from "@pages/ClinicalReportDetails/Providers/FocusedFieldProvider";
import { isGeneratingSection } from "@pages/ClinicalReportDetails/helper";
import { useClinicalReportDetailsData } from "@providers/ClinicalReportDetailsProvider";
import { useReport } from "@providers/ReportProvider";
import { useSidebarView } from "@providers/SidebarViewProvider/useSidebarView";

const SECTIONS_GENERATING_TOOLTIP = "All sections on the page must finish generating before continuing";

/** Reason shown on the disabled Complete/Sign action, or "" when it's enabled. */
const getCompleteDisabledTooltip = (isSectionGenerating: boolean, reviewGateReason: string | undefined): string => {
  if (isSectionGenerating) return SECTIONS_GENERATING_TOOLTIP;
  return reviewGateReason ?? "";
};

interface LoadingViewInput {
  shouldShowPreThinkingScreen: boolean;
  showDisclaimer: boolean;
  confirmDisclaimer: () => void;
  isReportQaScannerEnabled: boolean;
  isReportCompleted: boolean;
  hasReport: boolean;
  loadingState: "blank" | "skeleton" | "loaded";
  isReviewPanelVisible: boolean;
  suggestionsScrollRef: React.RefObject<HTMLDivElement | null>;
}

const renderLoadingView = (input: LoadingViewInput): React.ReactElement | null => {
  // The disclaimer opens on load, over the PreThinkingScreen or a blank page, and stays until Confirm.
  // One wrapper for both keeps the dialog mounted when the PreThinkingScreen finishes.
  if (input.shouldShowPreThinkingScreen || input.showDisclaimer) {
    return (
      <div className={clsx("flex w-full flex-col", !input.shouldShowPreThinkingScreen && "bg-limestone-50")}>
        {input.shouldShowPreThinkingScreen ? <PreThinkingScreen /> : <BlankState />}
        <SuggestionDisclaimer isOpen={input.showDisclaimer} onConfirm={input.confirmDisclaimer} />
      </div>
    );
  }
  const qaActive = input.isReportQaScannerEnabled && !input.isReportCompleted;
  const progressiveReady = qaActive && input.hasReport;
  if (input.loadingState === "blank" && progressiveReady) return <BlankState />;
  if (input.loadingState === "skeleton" && progressiveReady) {
    return (
      <SkeletonState
        isReviewPanelVisible={input.isReviewPanelVisible}
        suggestionsScrollRef={input.suggestionsScrollRef}
      />
    );
  }
  return null;
};

// Inner component that uses the suggestion context
const ReviewAndSignContent: React.FC = () => {
  const {
    reportSections,
    isReportCompleted,
    previousAssessmentReports,
    isReassessment,
    isInitialAssessment,
    setStepHeaderCompleteAction,
  } = useClinicalReportDetailsData();
  const { report, reportQuery } = useReport();

  const isReportQaScannerEnabled = useIsQaScannerEnabled();
  const isReviewPanelVisible = useIsReviewPanelVisible();
  const { view } = useSidebarView();
  const { state: session } = useReviewSession();
  const { loadedItems, generationStatus: suggestionGenerationStatus } = useReviewData();
  const { isBlocked: isReviewGateBlocked, tooltipReason: reviewGateReason } = useReviewGate();

  const { showDisclaimer, confirmDisclaimer, timeoutReached } = useFirstTimeDisclaimer({
    isReportCompleted,
  });

  const { reportId } = useParams<{ reportId: string }>();
  const analyticsReportType: "reassessment" | "diagnosis_builder" =
    report?.reportType === ApiReportReportTypeChoices.DiagnosisEvaluation ? "diagnosis_builder" : "reassessment";
  // Full reportContext so report_completed carries the same template fields
  // the wizard-level report_opened + wizard_step_* events already do.
  const { captureReportCompleted } = useReportAnalytics({
    report_id: reportId ?? "",
    report_type: analyticsReportType,
    template_id: report?.reportTemplate?.id,
    template_name: report?.reportTemplate?.name,
    template_payor_type: report?.reportTemplate?.payorType ?? undefined,
    template_export_framework: report?.reportTemplate?.exportFramework ?? undefined,
  });

  const { isCompletionDialogOpen, setIsCompletionDialogOpen, isGenerating, setIsGenerating, handleSignReport } =
    useSignatureHandler({
      isCompleted: isReportCompleted,
      report: report,
      refetchData: reportQuery.refetch,
      onReportCompleted: () =>
        captureReportCompleted({
          total_days_to_complete: computeTotalDaysToComplete(report?.createdDate),
          insurance_flags_remaining: countInsuranceFlagsRemaining(loadedItems),
        }),
    });

  const flags = useFlags<FeatureFlags>();
  const navigate = useNavigate();
  const {
    runBackfill,
    isBackfilling,
    backfillProgress,
    captureJobs,
    onCaptureProgress,
    onCaptureComplete,
    blockedItems,
    clearBlockedItems,
  } = useReassessmentChartBackfill(reportId);
  const isSectionGenerating = isGeneratingSection(reportSections);
  // Left scrollable report container (source of truth)
  const sectionFieldRef = useRef<HTMLDivElement | null>(null);
  // Right scrollable suggestions wrapper
  const suggestionsScrollRef = useRef<HTMLDivElement | null>(null);
  const reviewColumnRef = useRef<HTMLDivElement | null>(null);
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);

  const [loadingState, setLoadingState] = useState<"blank" | "skeleton" | "loaded">("blank");
  const [preThinkingScreenJustFinished, setPreThinkingScreenJustFinished] = useState<boolean>(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState<boolean>(false);
  const [selectedExportType, setSelectedExportType] = useState<SelectItem>(EXPORT_OPTIONS[0]);

  const clientName = `${report?.reportClientDetails?.clientFirstName || "N/A"} ${report?.reportClientDetails?.clientLastName || ""}`;
  const exportFramework = report?.reportTemplate?.exportFramework;
  const isDocxTemplaterExport = exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater;
  const isHandlebarsExport = exportFramework === ApiReportTemplateExportFrameworkChoices.Handlebars;
  const isUnifiedExport = report?.reportTemplate?.unifiedExport ?? false;
  // The template's `unifiedExport` field forces docxtemplater orgs through the
  // handlebars server-side export pipeline.
  const useHandlebarsPath = isHandlebarsExport || (isDocxTemplaterExport && isUnifiedExport);
  const useDocxTemplaterPath = isDocxTemplaterExport && !isUnifiedExport;

  const onSignReport = async () => {
    // Dont auto set for docxtemplater since export is just-in-time on frontend
    if (!useDocxTemplaterPath) setIsGenerating(true);
    setIsCompletionDialogOpen(true);
  };

  const { handleExport, isExporting } = useReportExport({
    report: report,
    exportType: selectedExportType.id as ExportTypeEnum,
    clientName,
    onExportComplete: () => {
      setIsExportModalOpen(false);
    },
  });

  const { handleExport: handleDocxTemplaterExport, isExporting: isDocxTemplaterExporting } = useTemplateExport({
    reportData: report,
    onExportComplete: () => {
      setIsCompletionDialogOpen(false);
    },
  });

  const handleExportReport = () => {
    if (useDocxTemplaterPath) {
      handleDocxTemplaterExport();
      return;
    }
    setIsCompletionDialogOpen(false);
    if (useHandlebarsPath) {
      setIsExportModalOpen(true);
      return;
    }
    handleExport();
  };

  const scrollToDivById = useNavigateById();
  const scrollInSuggestionsView = isReviewPanelVisible && !isReportCompleted;

  const completeReassessment = async (): Promise<void> => {
    const missingRequired = reportSections
      ?.flatMap((section) => section?.sectionField?.edges ?? [])
      .map((edge) => edge?.node)
      .filter((f) => !!f?.templateSectionField?.isRequired && isFieldContentEmpty(f.fieldType, f.content));

    if (missingRequired.length > 0) {
      setShowRequiredErrors(true);
      missingRequired.forEach((f) => notifyWarn(`${f?.title ? f.title : "Field"} is required.`));
      const firstId = missingRequired[0]!.id;
      const scrollFn = scrollInSuggestionsView ? scrollToElementById : scrollToDivById;
      scrollFn(firstId);
      return;
    }

    setShowRequiredErrors(false);

    // Capture a chart image for any approved goal/target still missing one
    // before finalizing. A data-bearing chart that can't be captured blocks
    // signing (blockedItems drives the dialog below).
    const { ok } = await runBackfill();
    if (!ok) return;

    // Always flag isGenerating during a Reassessment completion so the
    // ReportCompletionDialog renders a loading state on its CTAs and the
    // user can't dismiss it onto the still-in-progress Review step.
    // useSignatureHandler.onSuccess flips this back to false once the
    // status-polling loop confirms the backend has finalized the report.
    setIsGenerating(true);
    setIsCompletionDialogOpen(true);
    await handleSignReport(null);
  };

  const handlePrimaryButtonAction = async () => {
    if (isReassessment) {
      await completeReassessment();
      return;
    }
    // For diagnosis evaluations and other report types, the signature
    // section (if the template has one) already rendered its fields inline
    // above — just finalize.
    await onSignReport();
    await handleSignReport();
  };

  // Publish the Complete action up to the parent header for Reassessment so
  // the stepper's "Complete" button can drive it directly. Initial assessments
  // and diagnosis evaluations keep the existing fixed-footer flow.
  //
  // `handlePrimaryButtonAction` closes over a lot of local state (sections,
  // setShowRequiredErrors, completion-dialog state, etc.) and gets a fresh
  // identity each render. Reading it through a ref lets the registered
  // callback always invoke the current version without churning the effect.
  const completeHandlerRef = useRef(handlePrimaryButtonAction);
  completeHandlerRef.current = handlePrimaryButtonAction;
  useEffect(() => {
    if (!isReassessment || isReportCompleted) return;
    setStepHeaderCompleteAction({
      onComplete: () => completeHandlerRef.current(),
      disabled: isSectionGenerating || isReviewGateBlocked,
      disabledTooltip: getCompleteDisabledTooltip(isSectionGenerating, reviewGateReason),
    });
    return () => setStepHeaderCompleteAction(null);
  }, [
    isReassessment,
    isReportCompleted,
    isSectionGenerating,
    isReviewGateBlocked,
    reviewGateReason,
    setStepHeaderCompleteAction,
  ]);

  const handlePreviousAssessmentClicked = (learnerFile: LearnerFileType) => {
    let pdfFile;
    if (learnerFile.mimeType === "application/pdf") {
      pdfFile = learnerFile;
    } else {
      pdfFile = previousAssessmentReports.find(
        (report) => report.parentFile?.id === learnerFile.id && report.mimeType === "application/pdf",
      );
    }
    if (pdfFile) {
      setAssessmentFileForSearch(pdfFile);
      setAssessmentSearchOpen(true);
    }
  };

  const [assessmentSearchOpen, setAssessmentSearchOpen] = useState<boolean>(false);
  const [assessmentFileForSearch, setAssessmentFileForSearch] = useState<LearnerFileType | null>(null);

  // Two-way scroll sync between left report and right suggestions panel
  useScrollSync({
    leftRef: sectionFieldRef,
    rightRef: suggestionsScrollRef,
    activeFieldId: session.active?.fieldId ?? null,
  });
  const shouldShowPreThinkingScreen =
    isReportQaScannerEnabled && report && !isReportCompleted && suggestionGenerationStatus === null && !timeoutReached;

  // Progressive loading states: blank (300ms) -> skeleton (300ms) -> loaded
  // Only starts AFTER PreThinkingScreen finishes
  useEffect(() => {
    // Don't start if PreThinkingScreen is still showing
    if (shouldShowPreThinkingScreen) {
      return;
    }

    // Only start progressive loading if PreThinkingScreen just finished
    if (!preThinkingScreenJustFinished) {
      // If we haven't shown PreThinkingScreen, go straight to loaded
      setLoadingState("loaded");
      return;
    }

    // Start progressive loading sequence after PreThinkingScreen
    setLoadingState("blank");

    // After 300ms, show skeletons
    const skeletonTimeout = setTimeout(() => {
      setLoadingState("skeleton");
    }, 300);

    // After 600ms total (300ms blank + 300ms skeleton), show loaded content
    const loadedTimeout = setTimeout(() => {
      setLoadingState("loaded");
      setPreThinkingScreenJustFinished(false); // Reset flag after loading completes
    }, 600);

    return () => {
      clearTimeout(skeletonTimeout);
      clearTimeout(loadedTimeout);
    };
  }, [shouldShowPreThinkingScreen, preThinkingScreenJustFinished]);

  // Trigger progressive loading when timeout is reached (PreThinkingScreen finishes)
  useEffect(() => {
    if (timeoutReached) {
      setPreThinkingScreenJustFinished(true);
      setLoadingState("blank");
    }
  }, [timeoutReached]);

  // Reassessment has no footer row (its Complete button sits in the step header). The review bar finds the
  // footer row itself and sits above it.
  const showNonReassessmentFooterUI = !isReportCompleted && !isReassessment;

  const loadingView = renderLoadingView({
    shouldShowPreThinkingScreen,
    showDisclaimer,
    confirmDisclaimer,
    isReportQaScannerEnabled,
    isReportCompleted,
    hasReport: !!report,
    loadingState,
    isReviewPanelVisible,
    suggestionsScrollRef,
  });
  if (loadingView) return loadingView;

  const showLegacyReportSettings = shouldShowLegacyReportSettings(flags, { isInitialAssessment, isReassessment });
  const isSuggestionsLayoutActive = isReviewPanelVisible && !isReportCompleted;

  return (
    <FocusedFieldManagerProvider showRequiredErrors={showRequiredErrors}>
      <LayoutBody id="ClinicalReport">
        <LayoutMain ref={sectionFieldRef} className="py-4">
          {showLegacyReportSettings && (
            <div className="flex items-center justify-between pb-4">
              <ReportSettings reportIsCompleted={isReportCompleted} report={report} refetchData={reportQuery.refetch} />
            </div>
          )}
          {/* The "signature" ReportSections row (in-section digital_signature/
              signature_license_number/etc. fields, written by SignReport) is
              part of reportSections like any other section, so the map above
              already renders it read-only when the report is completed —
              for all 3 report types, matching how Reassessment has always
              relied on this same generic rendering (PROA-2308). */}
          {reportSections.map((section) => (
            <ReportSection key={section.id} section={section} disabled={isReportCompleted} />
          ))}
        </LayoutMain>
        {view === "toc" && (
          <LayoutAside className="mt-8 pt-2 animate-panel-in motion-reduce:animate-none">
            {isReassessment && (
              <PreviousAssessmentReportCollapsible handleFileClicked={handlePreviousAssessmentClicked} />
            )}
            <AssessmentReportsCollapsible isReadOnly={true} />
            <StickyNavCollapsible />
          </LayoutAside>
        )}
        {isSuggestionsLayoutActive && (
          <div className="flex-1 -translate-x-8 animate-panel-in motion-reduce:animate-none">
            <ReviewColumn
              scrollRef={suggestionsScrollRef}
              containerRef={reviewColumnRef}
              containerId="ClinicalReport"
              panelClassName="w-full col-span-3"
            />
          </div>
        )}
      </LayoutBody>
      {/* Outside the column, so it can drop away after the column has gone. */}
      <ReviewBar open={isSuggestionsLayoutActive} anchorRef={reviewColumnRef} />

      {/* For Reassessment, the Complete button is hoisted up to the
            step-progress header so all step actions live in one place — the
            fixed footer is skipped. Initial Assessment / Diagnosis Eval
            flows keep their footer. */}
      {showNonReassessmentFooterUI && (
        <ClinicalReportFooterButtonRow
          primaryButtonDisabled={isSectionGenerating || isReviewGateBlocked}
          primaryButtonDisabledTooltipReason={getCompleteDisabledTooltip(isSectionGenerating, reviewGateReason)}
          primaryButtonTitle="Sign and Complete"
          primaryButtonAction={handlePrimaryButtonAction}
          skipNavigate
          ehrSyncAlerts={report?.ehrSyncAlerts}
        />
      )}
      <ReportCompletionDialog
        open={isCompletionDialogOpen}
        clientName={clientName}
        clientId={report?.learner?.id}
        onExportReport={handleExportReport}
        onViewCompletedReport={() => {
          setIsCompletionDialogOpen(false);
        }}
        isGenerating={isGenerating || isDocxTemplaterExporting}
      />
      <ReportExportDialog
        open={isExportModalOpen}
        setOpen={() => setIsExportModalOpen(false)}
        onExport={handleExport}
        selectedExportType={selectedExportType}
        setSelectedExportType={setSelectedExportType}
        isExporting={isExporting}
        report={report}
        onRetrySuccess={async () => {
          await reportQuery.refetch();
        }}
        isReportLoading={reportQuery.isFetching}
      />
      <AssessmentSlideOver
        title="Previous Assessment Report"
        open={assessmentSearchOpen}
        setOpen={setAssessmentSearchOpen}
      >
        <AssessmentReportSearch learnerFile={assessmentFileForSearch} />
      </AssessmentSlideOver>

      {captureJobs && (
        <OffscreenChartCaptureRunner jobs={captureJobs} onProgress={onCaptureProgress} onComplete={onCaptureComplete} />
      )}

      <Dialog
        open={isBackfilling}
        setOpen={() => {}}
        title="Preparing charts"
        showCloseButton={false}
        backdrop="opaque"
        primaryButton={undefined}
        secondaryButton={undefined}
      >
        <Paragraph size="medium" colorType="primary">
          {backfillProgress ? `Capturing charts ${backfillProgress.done}/${backfillProgress.total}…` : "Preparing…"}
        </Paragraph>
      </Dialog>

      <Dialog
        open={blockedItems.length > 0}
        setOpen={(next) => {
          if (!next) clearBlockedItems();
        }}
        title="Charts need attention before signing"
        size="lg"
        primaryButton={{
          text: "Go to goals",
          onClick: () => {
            clearBlockedItems();
            navigate("../report-page");
          },
        }}
        secondaryButton={{ text: "Close", onClick: clearBlockedItems }}
      >
        <div className="flex flex-col gap-3 py-2 text-text-primary">
          <Paragraph size="medium" colorType="primary">
            These goals and targets have progress data, but their chart image couldn&apos;t be captured. Open each one
            to review its chart, then try completing again.
          </Paragraph>
          <ul className="list-disc pl-5 text-sm">
            {blockedItems.map((item) => (
              <li key={`${item.kind}:${item.id}`}>
                {item.name} <span className="text-text-tertiary">({item.kind})</span>
              </li>
            ))}
          </ul>
        </div>
      </Dialog>
    </FocusedFieldManagerProvider>
  );
};

export const ReviewAndSign: React.FC = () => {
  const isReportQaScannerEnabled = useIsQaScannerEnabled();
  const { isReassessment } = useClinicalReportDetailsData();

  // Redirect to report-page unless we have QA scanner or the reassessment flow
  if (!isReportQaScannerEnabled && !isReassessment) {
    return <Navigate to="../report-page" replace />;
  }

  return <ReviewAndSignContent />;
};
